import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled from 'styled-components';

import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../core/src/helpers/interaction/interactionHelpers';
import {ClickEventHandler, RendererOf} from '../../../../core/src/helpers/react/reactHelpers';
import {TooltipContext} from './tooltipContext';
import {TooltipCoordinatorAnchorRenderer} from './tooltipCoordinatorAnchorRenderer';
import {UseTooltipCoordinatorOptions} from './tooltipCoordinatorTypes';
import {useTooltipCoordinator} from './useTooltipCoordinator';

/*
 * Props.
 */

export interface TooltipCoordinatorProps extends UseTooltipCoordinatorOptions, InteractionComponentProps {
  className?: string;
  isInline?: boolean;
  onClick?: ClickEventHandler;
  /** Renders the target with additional information, will ignore children passed in */
  renderTarget?: RendererOf<boolean>;
  iconColor?: string;
  iconHoverColor?: string;
  children?: ReactNode;
}

/*
 * Style.
 */

export const StyledBlockWrapper = styled.div`
  min-width: 0;
`;

export const StyledInlineWrapper = styled.span`
  display: inline-block;
  vertical-align: bottom;
`;

/*
 * Component.
 */

export const TooltipCoordinator: FC<PropsWithChildren<TooltipCoordinatorProps>> = (props) => {
  const {anchorRef, contextValue, handlers, tooltipElement, isVisible} =
    useTooltipCoordinator<HTMLDivElement>(props);

  const Wrapper = props.isInline ? StyledInlineWrapper : StyledBlockWrapper;

  return (
    <Wrapper
      ref={anchorRef}
      className={props.className}
      onClick={props.onClick}
      onMouseEnter={handlers.onMouseEnter}
      onMouseLeave={handlers.onMouseLeave}
      onMouseDown={handlers.onMouseDown}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buildInteractionIdProps(props.interactionId)}
    >
      <TooltipContext.Provider value={contextValue}>
        <TooltipCoordinatorAnchorRenderer
          isVisible={isVisible}
          iconColor={props.iconColor}
          iconHoverColor={props.iconHoverColor}
          renderTarget={props.renderTarget}
        >
          {props.children}
        </TooltipCoordinatorAnchorRenderer>
      </TooltipContext.Provider>
      {tooltipElement}
    </Wrapper>
  );
};
